import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Title from 'components/base/Title'
import Paragraph from 'components/base/Paragraph'
import ResponsiveImage from 'components/base/ResponsiveImage'
import ProgressiveImage from 'components/base/ProgressiveImage'
import ButtonRow from 'components/base/Button/Row'
import Link, { Theme as LinkTheme } from 'components/base/Link'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import LocationsGroup from 'components/blocks/LocationsGroup'

import StoreBeaverton from 'assets/images/locations/beaverton.webp'
import StoreBeaverton2x from 'assets/images/locations/beaverton@2x.webp'
import StoreBeaverton64 from 'assets/images/locations/beaverton-placeholder'
import StoreGrantsPass from 'assets/images/locations/grants-pass.webp'
import StoreGrantsPass2x from 'assets/images/locations/grants-pass@2x.webp'
import StoreGrantsPass64 from 'assets/images/locations/grants-pass-placeholder'
import StoreMedford from 'assets/images/locations/medford.webp'
import StoreMedford2x from 'assets/images/locations/medford@2x.webp'
import StoreMedford64 from 'assets/images/locations/medford-placeholder'
import StoreEugene from 'assets/images/locations/eugene.webp'
import StoreEugene2x from 'assets/images/locations/eugene@2x.webp'
import StoreEugene64 from 'assets/images/locations/eugene-placeholder'
import HoursNotification from 'components/blocks/HoursNotification'

import {
  events,
  useGoogleTagManager,
} from 'hooks/useGoogleTagManager'

const ViewLocationButton = ({
  locationName,
  to,
}: {
  locationName: string,
  to: string,
}) => {
  const { pushDataLayerEvent } = useGoogleTagManager()

  return (
    <Link
      theme={LinkTheme.button}
      to={to}
      onClick={() => pushDataLayerEvent({
        ...events.cta_view_location,
        location_name: locationName,
      })}
    >
      View Location Page
    </Link>
  )
}

const Storefronts: React.FC = (): React.ReactElement => (
  <>
    <HeaderMeta
      title='Storefronts | Insurance Lounge'
      description='Contact our Insurance Lounge offices in Beaverton, Eugene, Grants Pass or Medford and get a free quote on home, auto, life, or medical insurance!'
      ogImg='/images/og-images/Lounges.webp'
    />
    <PageHero theme={PageHeroTheme.locations} right={true}>
      A Revolutionary Way to Shop For and Buy Insurance
    </PageHero>
    <Content id='storefronts' margined={true} center={true}>
      <Content short={true} center={true} margined={true}>
        <Title level={2}>
          We would love to help you at one of our storefronts!
        </Title>
      </Content>
      <Content short={true} center={true}>
        <Paragraph>
          Each Insurance Lounge location offers clients convenience and
          choice in a cool, comfortable environment.
        </Paragraph>
        <Paragraph>
          Whether you quote yourself using our powerful comparison
          technology, or you work with one of our no-commission Insurance
          Concierges<sup>®</sup>, you can be sure you’re getting the most
          value out of your insurance coverage from one (or more) of our 100+
          carrier partners.
        </Paragraph>
        <Paragraph>
          Each storefront is open seven days a week to make it easy for
          clients to shop whenever fits their schedule.
        </Paragraph>
      </Content>
      <LocationsGroup />
    </Content>
    <Content id='beaverton' theme={ContentTheme.yellow} center={true}>
      <Link theme={LinkTheme.unstyled} to='/locations/storefronts/beaverton'>
        <Title level={2}>Beaverton, OR</Title>
        <ResponsiveImage hasMargin={true} width720={true}>
          <ProgressiveImage
            src={StoreBeaverton}
            src2x={StoreBeaverton2x}
            placeholder={StoreBeaverton64}
            alt='Beaverton Store'
          />
        </ResponsiveImage>
      </Link>
      <Content short={true} center={true}>
        <Paragraph>
          Located in the Timberland Town Center, this is our fourth Insurance
          Lounge store in Beaverton, Oregon.
        </Paragraph>
        <Title level={3} noMargin={true}>
          Address
        </Title>
        <Paragraph>
          11835 NW Cedar Falls Drive, Suite 115
          <br />
          Beaverton, OR 97229
        </Paragraph>
        <Title level={3} noMargin={true}>
          Hours
        </Title>
        <Paragraph>
          Monday ⁠– Friday
          <br />
          9:00am ⁠– 6:00pm
        </Paragraph>
        <Paragraph>
          Saturday ⁠– Sunday
          <br />
          10:00am ⁠– 5:00pm
        </Paragraph>
        <HoursNotification />
        <ButtonRow>
          <ViewLocationButton
            locationName='beaverton_or'
            to='/locations/storefronts/beaverton'
          />
        </ButtonRow>
      </Content>
    </Content>
    <Content id='eugene' margined={true} width720={true} center={true}>
      <Link theme={LinkTheme.unstyled} to='/locations/storefronts/eugene'>
        <Title level={2}>Eugene, OR</Title>
        <ResponsiveImage hasMargin={true} width720={true}>
          <ProgressiveImage
            src={StoreEugene}
            src2x={StoreEugene2x}
            placeholder={StoreEugene64}
            alt='Eugene Store'
          />
        </ResponsiveImage>
      </Link>
      <Content short={true} center={true}>
        <Paragraph>
          At Insurance Lounge in Eugene, OR, we focus on technology and innovation to make insurance shopping easy & convenient. Stop by to check out our unique store and experience managing your insurance in a whole new way!
        </Paragraph>
        <Title level={3} noMargin={true}>
          Address
        </Title>
        <Paragraph>
          440 Coburg Rd Suite 200
          <br />
          Eugene OR 97401
        </Paragraph>
        <Title level={3} noMargin={true}>
          Hours
        </Title>
        <Paragraph>
          Monday ⁠– Friday
          <br />
          9:00am ⁠– 6:00pm
        </Paragraph>
        <Paragraph>
          Saturday ⁠– Sunday
          <br />
          Closed
        </Paragraph>
        <HoursNotification />
        <ButtonRow>
          <ViewLocationButton
            locationName='eugene_or'
            to='/locations/storefronts/eugene'
          />
        </ButtonRow>
      </Content>
    </Content>
    <Content id='grants-pass' theme={ContentTheme.yellow} center={true}>
      <Link theme={LinkTheme.unstyled} to='/locations/storefronts/grants-pass'>
        <Title level={2}>Grants Pass, OR</Title>
        <ResponsiveImage hasMargin={true} width720={true}>
          <ProgressiveImage
            src={StoreGrantsPass}
            src2x={StoreGrantsPass2x}
            placeholder={StoreGrantsPass64}
            alt='Grants Pass Store'
          />
        </ResponsiveImage>
      </Link>
      <Content short={true} center={true}>
        <Paragraph>
          Our Grants Pass location is the corporate office for Insurance
          Lounge and also one of our main sales offices in Oregon.
        </Paragraph>
        <Title level={3} noMargin={true}>
          Address
        </Title>
        <Paragraph>
          1221 Northeast 7th Street
          <br />
          Grants Pass, OR 97526
        </Paragraph>
        <Title level={3} noMargin={true}>
          Hours
        </Title>
        <Paragraph>
          Monday ⁠– Friday
          <br />
          9:00am ⁠– 5:00pm
        </Paragraph>
        <Paragraph>
          Saturday ⁠– Sunday
          <br />
          Closed
        </Paragraph>
        <HoursNotification />
        <ButtonRow>
          <ViewLocationButton
            locationName='grants_pass_or'
            to='/locations/storefronts/grants-pass'
          />
        </ButtonRow>
      </Content>
    </Content>
    <Content id='las-vegas' margined={true} width720={true} center={true}>
      <Link theme={LinkTheme.unstyled} to='/locations/storefronts/las-vegas'>
        <Title level={2}>Las Vegas, NV</Title>
        {/* <ResponsiveImage hasMargin={true} width720={true}>
          <ProgressiveImage
            src={StoreMedford}
            src2x={StoreMedford2x}
            placeholder={StoreMedford64}
            alt='Medford Store'
          />
        </ResponsiveImage> */}
      </Link>
      <Content short={true} center={true}>
        <Paragraph>
          Stop by our Las Vegas insurance agency and shop insurance rates
          across multiple providers using our quoting kiosks.
        </Paragraph>
        <Title level={3} noMargin={true}>
          Address
        </Title>
        <Paragraph>
          3572 St. Rose Parkway Suite 100
          <br />
          Henderson, NV 89052
        </Paragraph>
        <Title level={3} noMargin={true}>
          Hours
        </Title>
        <Paragraph>
          Monday ⁠– Friday
          <br />
          9:00am ⁠– 5:00pm
        </Paragraph>
        <HoursNotification />
        <ButtonRow>
          <ViewLocationButton
            locationName='las_vegas_nv'
            to='/locations/storefronts/las-vegas'
          />
        </ButtonRow>
      </Content>
    </Content>
    <Content id='medford' theme={ContentTheme.yellow} center={true}>
      <Link theme={LinkTheme.unstyled} to='/locations/storefronts/medford'>
        <Title level={2}>Medford, OR</Title>
        <ResponsiveImage hasMargin={true} width720={true}>
          <ProgressiveImage
            src={StoreMedford}
            src2x={StoreMedford2x}
            placeholder={StoreMedford64}
            alt='Medford Store'
          />
        </ResponsiveImage>
      </Link>
      <Content short={true} center={true}>
        <Paragraph>
          Stop by our Medford insurance agency and shop insurance rates
          across multiple providers using our quoting kiosks.
        </Paragraph>
        <Title level={3} noMargin={true}>
          Address
        </Title>
        <Paragraph>
          151 Rossanley Drive, Suite 105
          <br />
          Medford, OR 97504
        </Paragraph>
        <Title level={3} noMargin={true}>
          Hours
        </Title>
        <Paragraph>
          Monday ⁠– Friday
          <br />
          9:00am ⁠– 6:00pm
        </Paragraph>
        <Paragraph>
          Saturday ⁠– Sunday
          <br />
          10:00am ⁠– 5:00pm
        </Paragraph>
        <HoursNotification />
        <ButtonRow>
          <ViewLocationButton
            locationName='medford_or'
            to='/locations/storefronts/medford'
          />
        </ButtonRow>
      </Content>
    </Content>
  </>
)

export default Storefronts
